import React, { useState, useRef } from "react";
import { default as ReactSelect, components, InputAction } from "react-select";
import { Controller } from "react-hook-form";
import {
  FormControl,
  Grid,
  Typography
} from "@mui/material";

const MultiSelect = (props) => {
  const [selectInput, setSelectInput] = useState("");
  const isAllSelected = useRef(false);
  const selectAllLabel = useRef("Select all");
  const allOption = { value: "*", label: selectAllLabel.current };

  const filterOptions = (options, input) =>
    options?.filter(({ label }) =>
      label.toLowerCase().includes(input.toLowerCase())
    );

  const comparator = (v1, v2) => v1.value - v2.value;

  let filteredOptions = filterOptions(props.options, selectInput);
  let filteredSelectedOptions = filterOptions(props.value, selectInput);

  const Option = (props) => (
    <components.Option {...props}>
      {props.value === "*" &&
      !isAllSelected.current &&
      filteredSelectedOptions?.length > 0 ? (
        <input
          key={props.value}
          type="checkbox"
          ref={(input) => {
            if (input) input.indeterminate = true;
          }}
        />
      ) : (
        <input
          key={props.value}
          type="checkbox"
          checked={props.isSelected || isAllSelected.current}
          onChange={() => {}}
        />
      )}
      <label style={{ marginLeft: "5px" }}>{props.label}</label>
    </components.Option>
  );

  const Input = (props) => (
    <>
      {selectInput.length === 0 ? (
        <components.Input autoFocus={props.selectProps.menuIsOpen} {...props}>
          {props.children}
        </components.Input>
      ) : (
        <div style={{ border: "1px dotted gray" }}>
          <components.Input autoFocus={props.selectProps.menuIsOpen} {...props}>
            {props.children}
          </components.Input>
        </div>
      )}
    </>
  );

  const customFilterOption = ({ value, label }, input) =>
    (value !== "*" && label.toLowerCase().includes(input.toLowerCase())) ||
    (value === "*" && filteredOptions?.length > 0);

  const onInputChange = (inputValue, event) => {
    if (event.action === "input-change") setSelectInput(inputValue);
    else if (event.action === "menu-close" && selectInput !== "")
      setSelectInput("");
  };

  const onKeyDown = (e) => {
    if ((e.key === " " || e.key === "Enter") && !selectInput) e.preventDefault();
  };

  const handleChange = (selected) => {
    setSelectInput("")
    if (
      selected.length > 0 &&
      !isAllSelected.current &&
      (selected[selected.length - 1].value === allOption.value ||
        JSON.stringify(filteredOptions) ===
          JSON.stringify(selected.sort(comparator)))
    )
      return props.onChange(
        [
          ...(props.value ?? []),
          ...props.options.filter(
            ({ label }) =>
              label.toLowerCase().includes(selectInput?.toLowerCase()) &&
              (props.value ?? []).filter((opt) => opt.label === label)
                .length === 0
          ),
        ].sort(comparator)
      );
    else if (
      selected.length > 0 &&
      selected[selected.length - 1].value !== allOption.value &&
      JSON.stringify(selected.sort(comparator)) !==
        JSON.stringify(filteredOptions)
    )
      return props.onChange(selected);
    else
      return props.onChange([
        ...props.value?.filter(
          ({ label }) =>
            !label.toLowerCase().includes(selectInput?.toLowerCase())
        ),
      ]);
  };

  const customStyles = {
    multiValueLabel: (def) => ({
      ...def,
      backgroundColor: "lightgray",
    }),
    multiValueRemove: (def) => ({
      ...def,
      backgroundColor: "lightgray",
    }),
    valueContainer: (base) => ({
      ...base,
      maxHeight: "65px",
      overflow: "auto",
    }),
    option: (styles, { isSelected, isFocused }) => ({
      ...styles,
      backgroundColor:
        isSelected && !isFocused
          ? null
          : isFocused && !isSelected
          ? styles.backgroundColor
          : isFocused && isSelected
          ? "#DEEBFF"
          : null,
      color: isSelected ? null : null,
    }),
    menu: (def) => ({ ...def, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      border: "1px solid gray",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid gray",
      },
    }),
  };



  if (props.isSelectAll && props.options.length !== 0) {
    isAllSelected.current =
      JSON.stringify(filteredSelectedOptions) ===
      JSON.stringify(filteredOptions);

    if (filteredSelectedOptions?.length > 0) {
      if (filteredSelectedOptions?.length === filteredOptions?.length)
        selectAllLabel.current = `All (${filteredOptions.length}) selected`;
      else
        selectAllLabel.current = `${filteredSelectedOptions?.length} / ${filteredOptions.length} selected`;
    } else selectAllLabel.current = "Select all";

    allOption.label = selectAllLabel.current;

    return (
      <ReactSelect
        {...props}
        inputValue={selectInput}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        options={[allOption, ...props.options]}
        onChange={handleChange}
        components={{
          Option: Option,
          Input: Input,
          ...props.components,
        }}
        filterOption={customFilterOption}
        menuPlacement={props.menuPlacement ?? "auto"}
        styles={customStyles}
        isMulti
        closeMenuOnSelect={false}
        tabSelectsValue={false}
        backspaceRemovesValue={false}
        hideSelectedOptions={false}
        blurInputOnSelect={false}
      />
    );
  }

  return (
    <ReactSelect
      {...props}
      inputValue={selectInput}
      onInputChange={onInputChange}
      filterOption={customFilterOption}
      components={{
        Input: Input,
        ...props.components,
      }}
      menuPlacement={props.menuPlacement ?? "auto"}
      onKeyDown={onKeyDown}
      tabSelectsValue={false}
      hideSelectedOptions={true}
      backspaceRemovesValue={false}
      blurInputOnSelect={true}
    />
  );
};

export function MultiSelectField({
  label,
  width,
  disabled,
  control,
  rules,
  name,
  labelStyle,
  callback,
  madatory = false,
  textfieldStyles,
  options = [],
  placeholder = "Select...",
  onInputChangeValue = (f) => f,
  ...rest
}) {

  const [inputValue, setInputValue] = useState("");

  return (
    <FormControl sx={{ width: width || "100%", gap: 0 }}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        defaultValue={[]}
        render={({ field, fieldState }) => (
          <Grid container rowSpacing={label ? "4px" : 0}>

            {/* Label */}
            <Grid item xs={12}>
              <Typography
                style={{
                  textAlign: "start",
                  fontFamily: "Figtree",
                  ...labelStyle,
                }}
              >
                {madatory ? (
                  <>
                    {label}
                    <span style={{ color: "red", marginLeft: 4 }}>*</span>
                  </>
                ) : (
                  label
                )}
              </Typography>
            </Grid>

            {/* Multi Select */}
            <Grid item xs={12}>
              <ReactSelect
                {...rest}
                isMulti
                isDisabled={disabled}
                options={options}
                value={options.filter((opt) =>
                  (field.value || []).includes(opt.value)
                )}
                inputValue={inputValue}
                placeholder={placeholder}

                onInputChange={(val, meta) => {
                  onInputChangeValue(val)
                  if (meta.action === "input-change") setInputValue(val);
                  if (meta.action === "menu-close") setInputValue("");
                }}

                onChange={(selected) => {
                  const values = selected ? selected.map((item) => item.value) : [];

                  field.onChange(values);

                  if (callback) callback(values);
                }}

                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "40px",
                    border: fieldState.error
                      ? "1px solid red"
                      : "1px solid gray",
                    boxShadow: "none",
                    backgroundColor: disabled ? "#f5f5f5" : "white",
                    ...textfieldStyles,
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    maxHeight: "65px",
                    overflow: "auto",
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />

              {/* Error */}
              {fieldState.error && (
                <Typography
                  style={{
                    color: "red",
                    fontSize: "11.5px",
                    marginTop: "4px",
                  }}
                >
                  {fieldState.error.message}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      />
    </FormControl>
  );
}

export default MultiSelect;
